import { Form, Input, Steps, Button, Select, Space, Typography, App, DatePicker, Segmented, Modal, Progress, Radio, theme } from 'antd';
import React, { useEffect, useState } from 'react';
import { _T, Localization } from '../../../../base-react/src/Localization';
import { apis, hooks, models } from "@vote4dance/judging-sdk";
import momenttz from 'moment-timezone';
var Api = apis.Api;
var Stream = hooks.streams.Stream;
var FederationSource = models.federations.FederationSource;
var CompetitionSource = models.competitions.CompetitionsSource;
import { BodyContainer, HeaderContainer } from '../../../../base-react/components/Container';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCompetition } from '../../../../base-react/hooks/Competition';
import CustomerBilling from './components/CustomerBilling';
import dayjs from 'dayjs';
import { Loading } from '../../../../base-react/components/Loading';
import { bootstrapCompetitionDefaults } from './bootstrap/defaults';
import { useUserSettings } from '../../../../base-react/hooks/Storage';
const { Option } = Select;
const { Title, Text } = Typography;
const dateFormat = 'YYYY-MM-DD';
export const CompetitionDetails = () => {
    var _a;
    const { token } = theme.useToken();
    const [isLoading, setIsLoading] = useState(false);
    const [dateMode, setDateMode] = useState('single');
    const [form] = Form.useForm();
    const { notification } = App.useApp();
    const { updateSettings } = useUserSettings();
    const [bootstrapProgressVisible, setBootstrapProgressVisible] = useState(false);
    const [bootstrapProgressValue, setBootstrapProgressValue] = useState(0);
    const [bootstrapProgressLabel, setBootstrapProgressLabel] = useState('');
    let [searchParams] = useSearchParams();
    const [currentStep, setCurrentStep] = useState(searchParams.get('step') ? parseInt(searchParams.get('step')) : 0);
    const [federations, loadingFederations] = Stream(FederationSource);
    let { event_id } = useParams();
    const [, event] = useCompetition(event_id);
    const [competition, setCompetition] = useState();
    const [showBillingInfo, setShowBillingInfo] = useState(false);
    const navigate = useNavigate();
    const defaultPracticeFederationId = (_a = federations === null || federations === void 0 ? void 0 : federations.find((federation) => federation.federation_tag === 'NONE')) === null || _a === void 0 ? void 0 : _a.federation_id;
    const isNewCompetition = !(competition === null || competition === void 0 ? void 0 : competition.competition_id);
    const federationSetupMode = Form.useWatch('federation_setup_mode', form);
    const showFederationSelect = !isNewCompetition || federationSetupMode === 'specific' || !defaultPracticeFederationId;
    const locationValue = Form.useWatch('competition_location', form);
    const countryValue = Form.useWatch('competition_country', form);
    const countryLabel = countryValue ? Localization.getCountryName(countryValue) : undefined;
    const mapsQuery = [locationValue, countryLabel].filter(Boolean).join(', ');
    const mapsUrl = mapsQuery
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`
        : undefined;
    const isBlankFieldValue = (value) => (value == null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0));
    const applyDefaultSetupPrefill = () => {
        var _a;
        const guessedCountry = form.getFieldValue('competition_country') || (competition === null || competition === void 0 ? void 0 : competition.competition_country) || Localization.guessUserCountry();
        const guessedTimezone = form.getFieldValue('competition_timezone') || (competition === null || competition === void 0 ? void 0 : competition.competition_timezone) || momenttz.tz.guess();
        const guessedCountryName = guessedCountry ? Localization.getCountryName(guessedCountry) : undefined;
        const fallbackLocation = guessedTimezone
            ? (_a = String(guessedTimezone).split('/').slice(-1)[0]) === null || _a === void 0 ? void 0 : _a.replace(/_/g, ' ')
            : undefined;
        const defaultCompetitionName = `${_T('competition.title')} ${dayjs().format(dateFormat)}`;
        const defaultCompetitionDate = dateMode === 'multi'
            ? [dayjs(), dayjs().add(1, 'day')]
            : dayjs();
        const bestGuessValues = {
            federation_id: form.getFieldValue('federation_id') || defaultPracticeFederationId,
            competition_level: 0,
            competition_name: defaultCompetitionName,
            competition_organizer: defaultCompetitionName,
            competition_date: defaultCompetitionDate,
            competition_short_description: defaultCompetitionName,
            competition_location: guessedCountryName || fallbackLocation || _T('competition.title'),
            competition_country: guessedCountry,
            competition_timezone: guessedTimezone
        };
        const currentValues = form.getFieldsValue(Object.keys(bestGuessValues));
        const valuesToSet = Object.entries(bestGuessValues).reduce((acc, [key, value]) => {
            if (value == null || !isBlankFieldValue(currentValues[key])) {
                return acc;
            }
            acc[key] = value;
            return acc;
        }, {});
        if (Object.keys(valuesToSet).length > 0) {
            form.setFieldsValue(valuesToSet);
        }
        if (valuesToSet.competition_level != null) {
            setShowBillingInfo(Number(valuesToSet.competition_level) > 0);
        }
    };
    // New competition setup
    useEffect(() => {
        if (!event_id) {
            setCompetition({
                competition_level: 0,
                competition_country: Localization.guessUserCountry(),
                competition_timezone: momenttz.tz.guess(),
                competition_publish_liveplacement: 2,
                competition_publish_timeout: null
            });
            setDateMode('single');
        }
    }, []);
    // Wait for existing competition data
    useEffect(() => {
        if (event) {
            setCompetition(event);
            setShowBillingInfo(event.competition_level > 0);
            setDateMode(event.competition_end_date && event.competition_end_date !== event.competition_date ? 'multi' : 'single');
        }
    }, [event]);
    useEffect(() => {
        if (!isNewCompetition || federationSetupMode !== 'default' || !defaultPracticeFederationId) {
            return;
        }
        const currentFederationId = form.getFieldValue('federation_id');
        if (isBlankFieldValue(currentFederationId)) {
            form.setFieldValue('federation_id', defaultPracticeFederationId);
        }
    }, [defaultPracticeFederationId, federationSetupMode, form, isNewCompetition]);
    const formPages = [
        [
            {
                name: 'federation_setup_mode',
                rules: [{ required: isNewCompetition, message: _T('common.required') }],
                hidden: !isNewCompetition,
                input: () => (React.createElement(Radio.Group, { style: { width: '100%' } },
                    React.createElement(Space, { orientation: "vertical", style: { width: '100%' }, size: 12 }, ['default', 'specific'].map((mode, index) => {
                        const selected = federationSetupMode === mode;
                        return (React.createElement(Radio, { key: mode, value: mode, style: { width: '100%', marginInlineEnd: 0 } },
                            React.createElement("div", { style: {
                                    width: '100%',
                                    border: `1px solid ${selected ? token.colorPrimaryBorder : token.colorBorder}`,
                                    backgroundColor: selected ? token.colorPrimaryBg : token.colorBgContainer,
                                    borderRadius: 8,
                                    padding: '12px 14px'
                                } },
                                React.createElement("div", { style: { fontWeight: 600, marginBottom: 4 } },
                                    index + 1,
                                    ". ",
                                    _T(`competition.details.federation_setup_mode.${mode}`)),
                                React.createElement(Text, { style: { color: selected ? token.colorText : token.colorTextSecondary } }, _T(`competition.details.federation_setup_mode.${mode}_description`)))));
                    }))))
            },
            {
                name: 'federation_id',
                rules: [{ required: true, message: _T('common.required') }],
                hidden: isNewCompetition && !showFederationSelect,
                input: () => (React.createElement(Select, null, federations && federations.map((federation, index) => (React.createElement(Option, { value: federation.federation_id, key: index }, federation.federation_name)))))
            }
        ],
        [
            {
                name: 'competition_name',
                rules: [{ required: true, message: _T('common.required') }],
                extra: _T('competition.details.event_name.extra'),
                input: () => (React.createElement(Input, { disabled: isLoading, placeholder: _T('competition.details.event_name.placeholder') }))
            },
            {
                name: 'competition_organizer',
                rules: [{ required: true, message: _T('common.required') }],
                input: () => (React.createElement(Input, { disabled: isLoading, placeholder: _T('competition.details.competition_organizer.placeholder') }))
            },
            {
                name: 'competition_date_mode',
                input: () => (React.createElement(Segmented, { value: dateMode, disabled: isLoading, options: [
                        { label: _T('competition.details.competition_date_mode.single'), value: 'single' },
                        { label: _T('competition.details.competition_date_mode.multi'), value: 'multi' }
                    ], onChange: (value) => {
                        const nextMode = value;
                        const currentValue = form.getFieldValue('competition_date');
                        if (nextMode === 'single') {
                            form.setFieldValue('competition_date', Array.isArray(currentValue) ? currentValue[0] : currentValue);
                        }
                        else {
                            const startDate = Array.isArray(currentValue) ? currentValue[0] : currentValue;
                            form.setFieldValue('competition_date', startDate ? [startDate, startDate.add(1, 'day')] : undefined);
                        }
                        setDateMode(nextMode);
                    } }))
            },
            {
                name: 'competition_date',
                rules: [{ required: true, message: _T('common.required') }],
                input: () => (dateMode === 'multi'
                    ? (React.createElement(DatePicker.RangePicker, { disabled: isLoading }))
                    : (React.createElement(DatePicker, { disabled: isLoading })))
            },
            {
                name: 'competition_contact',
                input: () => (React.createElement(Input, { type: "email", disabled: isLoading, placeholder: _T('competition.details.competition_contact.placeholder') }))
            },
            {
                name: 'competition_short_description',
                rules: [{ required: true, message: _T('common.required') }],
                extra: _T('competition.details.competition_short_description.extra'),
                input: () => (React.createElement(Input.TextArea, { rows: 3, maxLength: 150, disabled: isLoading, showCount: true }))
            },
            {
                name: 'competition_level',
                rules: [{ required: true, message: _T('common.required') }],
                extra: _T('competition.details.competition_level.extra'),
                input: () => (React.createElement(Select, null,
                    React.createElement(Option, { value: 0, key: 0 }, _T('competition.details.competition_level.' + 0)),
                    React.createElement(Option, { value: 1, key: 1 }, _T('competition.details.competition_level.' + 1))))
            }
        ],
        [
            {
                name: 'competition_location',
                rules: [{ required: true, message: _T('common.required') }],
                extra: mapsUrl ? (React.createElement("a", { href: mapsUrl, target: "_blank", rel: "noreferrer" }, _T('competition.details.competition_location.verify'))) : undefined,
                input: () => (React.createElement(Input, { disabled: isLoading }))
            },
            {
                name: 'competition_country',
                rules: [{ required: true, message: _T('common.required') }],
                input: () => (React.createElement(Select, { disabled: isLoading, showSearch: true, placeholder: _T('competition.details.competition_country.label'), optionFilterProp: "label", options: Object.keys(Localization.getCountryNames()).map((code) => ({
                        value: code,
                        label: Localization.getCountryName(code)
                    })) }))
            },
            {
                name: 'competition_timezone',
                rules: [{ required: true, message: _T('common.required') }],
                input: () => (React.createElement(Select, null, momenttz.tz.names().map((tz, index) => (React.createElement(Option, { value: tz, key: index }, `${tz} (${momenttz.tz(tz).format('Z')})`)))))
            }
        ],
        [
            {
                name: 'competition_billing_reference',
                rules: [{ required: showBillingInfo, message: _T('common.required') }],
                extra: _T('competition.details.competition_billing_reference.extra'),
                hidden: !showBillingInfo,
                input: () => (
                /* @ts-ignore */
                React.createElement(CustomerBilling, null))
            }
        ],
    ];
    const next = () => {
        setCurrentStep(currentStep + 1);
    };
    const prev = () => {
        setCurrentStep(currentStep - 1);
    };
    const getCurrentStepFieldNames = () => {
        return formPages[currentStep].map((formPageItem) => formPageItem.name);
    };
    const finalizeBootstrapAndNavigate = async ({ competitionId, bootstrapDateMode, startDate, endDate }) => {
        setBootstrapProgressVisible(true);
        setBootstrapProgressValue(20);
        setBootstrapProgressLabel(_T('competition.details.bootstrap.progress.creating_defaults'));
        try {
            await bootstrapCompetitionDefaults({
                competitionId,
                dateMode: bootstrapDateMode,
                startDate,
                endDate,
                dateFormat
            });
        }
        catch (err) {
            setBootstrapProgressVisible(false);
            notification.error({
                title: err.message
            });
            return false;
        }
        setBootstrapProgressValue(100);
        setBootstrapProgressLabel(_T('competition.details.bootstrap.progress.finalizing'));
        setBootstrapProgressVisible(false);
        updateSettings({
            managerOnboardingGuideStepInfo: `${competitionId}/0`
        });
        navigate(`../event/${competitionId}/competitions`, { state: { openGuide: true } });
        return true;
    };
    const onNext = async () => {
        if (currentStep === 0 && isNewCompetition && federationSetupMode === 'default') {
            try {
                await form.validateFields(getCurrentStepFieldNames());
            }
            catch {
                return;
            }
            applyDefaultSetupPrefill();
            form.submit();
            return;
        }
        form
            .validateFields(getCurrentStepFieldNames())
            .then(() => {
            next();
        });
    };
    const onValuesChange = (changedValues) => {
        const changeKeys = Object.keys(changedValues);
        if (changeKeys.includes('competition_level')) {
            const isPublicLevel = changedValues['competition_level'] > 0;
            setShowBillingInfo(isPublicLevel);
        }
    };
    const focusField = (fieldName) => {
        const fieldId = `new_competition_${fieldName}`;
        const direct = document.getElementById(fieldId);
        if (direct && typeof direct.focus === 'function') {
            direct.focus();
            return true;
        }
        const nested = document.querySelector(`#${fieldId} input, #${fieldId} textarea, #${fieldId} .ant-select-selection-search-input, #${fieldId} .ant-radio-input`);
        if (nested && typeof nested.focus === 'function') {
            nested.focus();
            return true;
        }
        return false;
    };
    const getFirstFieldNameForCurrentStep = () => {
        var _a;
        const stepFields = formPages[currentStep] || [];
        return (_a = stepFields.find((item) => !item.disabled && !item.hidden)) === null || _a === void 0 ? void 0 : _a.name;
    };
    useEffect(() => {
        if (loadingFederations || !competition || (competition === null || competition === void 0 ? void 0 : competition.competition_id)) {
            return;
        }
        const firstFieldName = getFirstFieldNameForCurrentStep();
        if (!firstFieldName) {
            return;
        }
        const timer = window.setTimeout(() => {
            focusField(firstFieldName);
        }, 0);
        return () => window.clearTimeout(timer);
    }, [competition, currentStep, loadingFederations, showBillingInfo]);
    const onFinish = async () => {
        var _a;
        setIsLoading(true);
        const fieldsValue = await form.validateFields();
        const formValues = { ...fieldsValue };
        delete formValues.competition_date_mode;
        delete formValues.federation_setup_mode;
        const dateValue = formValues['competition_date'];
        const startDate = Array.isArray(dateValue) ? dateValue[0] : dateValue;
        const endDate = Array.isArray(dateValue) ? dateValue[1] : startDate;
        const updateItem = {
            ...competition,
            ...formValues,
            competition_date: startDate
                ? startDate.format(dateFormat)
                : dayjs().format(dateFormat),
            competition_end_date: endDate
                ? endDate.format(dateFormat)
                : undefined,
            competition_federation: (_a = federations === null || federations === void 0 ? void 0 : federations.find(a => a.federation_id == fieldsValue['federation_id'])) === null || _a === void 0 ? void 0 : _a.federation_tag
        };
        setIsLoading(true);
        let result;
        let hasError = false;
        try {
            if (competition && competition.competition_id) {
                await Api.update(`${CompetitionSource.url}/${updateItem.competition_id}`, updateItem);
            }
            else {
                result = await Api.create(CompetitionSource.url, updateItem);
            }
        }
        catch (err) {
            notification.error({
                title: err.message
            });
            hasError = true;
        }
        setIsLoading(false);
        if (!hasError) {
            if (result) {
                if (!result.competition_id) {
                    navigate('../');
                    return;
                }
                await finalizeBootstrapAndNavigate({
                    competitionId: result.competition_id,
                    bootstrapDateMode: dateMode,
                    startDate,
                    endDate
                });
            }
            else {
                navigate('../');
            }
        }
    };
    const onChangeStep = (value) => {
        form
            .validateFields()
            .then(() => {
            setCurrentStep(value);
        });
    };
    const steps = [1, 2, 3, 4].map(i => ({
        title: _T('competition.details.steps.' + i)
    }));
    return (React.createElement(React.Fragment, null,
        React.createElement(HeaderContainer, null,
            React.createElement(Typography, null,
                React.createElement(Title, null, _T('competition.details.title')))),
        React.createElement(BodyContainer, null,
            React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                React.createElement(Steps, { items: steps, onChange: competition && competition.competition_id ? onChangeStep : undefined, current: currentStep, style: { marginBottom: 30 } }),
                (!competition || loadingFederations) ? (React.createElement(Loading, null)) : (React.createElement(React.Fragment, null,
                    React.createElement(Form, { name: "new_competition", form: form, onFinish: onFinish, onValuesChange: onValuesChange, labelCol: { span: 4 }, wrapperCol: { span: currentStep >= 3 ? 20 : 8 }, labelWrap: true, requiredMark: false, initialValues: {
                            ...competition,
                            federation_setup_mode: competition.competition_id ? 'specific' : 'default',
                            competition_date: competition.competition_date
                                ? (dateMode === 'multi'
                                    ? [
                                        dayjs(competition.competition_date),
                                        dayjs(competition.competition_end_date || competition.competition_date)
                                    ]
                                    : dayjs(competition.competition_date))
                                : undefined
                        } }, formPages.map((formPage, formPageIndex) => (formPage.filter((formPageItem, formPageItemIndex) => (!formPageItem.disabled))
                        .map((formPageItem, formPageItemIndex) => (React.createElement(Form.Item, { key: formPageIndex + formPageItemIndex, label: _T('competition.details.' + formPageItem.name + '.label'), name: formPageItem.name, extra: formPageItem.extra, hidden: formPageIndex != currentStep || formPageItem.hidden, valuePropName: formPageItem.valuePropName, getValueFromEvent: formPageItem.getValueFromEvent, getValueProps: formPageItem.getValueProps, rules: formPageItem.rules }, formPageItem.input())))))),
                    React.createElement("div", { style: { display: 'flex', width: '100%', justifyContent: 'flex-start', gap: 8 } },
                        currentStep > 0 && (React.createElement(Button, { onClick: () => prev() }, _T('common.previous'))),
                        currentStep < steps.length - 1 && (React.createElement(Button, { type: "primary", loading: currentStep === 0 && isLoading, onClick: () => onNext() }, _T('common.next'))),
                        currentStep == steps.length - 1 && (React.createElement(Button, { loading: isLoading, type: "primary", size: "large", onClick: () => form.submit() }, _T(competition.competition_id ? 'common.save' : 'common.create')))))))),
        React.createElement(Modal, { title: _T('competition.details.bootstrap.progress.title'), open: bootstrapProgressVisible, footer: null, closable: false, mask: { closable: false }, keyboard: false },
            React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
                React.createElement(Text, null, bootstrapProgressLabel),
                React.createElement(Progress, { percent: bootstrapProgressValue, status: "active" })))));
};
